import { FACET_ATTRIBUTES } from './facets-shared';
import { buildFinlandBaselineFacetFilter } from '../geocoding/finland-place-ids';

export type FacetFilterEntry = string | string[];

export type DisjunctiveFacetRequest = {
  /** The facet (FACET_ATTRIBUTES key) whose counts this request supplies. */
  attribute: string;
  /** The main search's facetFilters minus this facet's own include / "only" picks. */
  facetFilters: FacetFilterEntry[];
};

const FACET_ATTRIBUTE_SET = new Set<string>(FACET_ATTRIBUTES);
const PICK_ENTRY = /^(ONLY )?([A-Za-z0-9_]+):/;

/** The facet an include (`attr:value`) or exact-match (`ONLY attr:value`) string picks, else null. */
function pickedAttribute(entry: string): string | null {
  if (entry.startsWith('NOT ')) return null;
  const match = PICK_ENTRY.exec(entry);
  if (!match) return null;
  return FACET_ATTRIBUTE_SET.has(match[2]) ? match[2] : null;
}

/** The facet a whole facetFilters entry picks. An OR-group only counts when every member picks the
 * same facet; the compound Finland baseline (regionTags OR remoteEligibleCountryCodes) is the
 * app's own default scope, not a user pick, so it is never a pick even though it names regionTags. */
function entryPickedAttribute(entry: FacetFilterEntry, finlandBaselineGroup: string[]): string | null {
  if (typeof entry === 'string') return pickedAttribute(entry);
  if (entry.length === 0) return null;
  if (entry.length === finlandBaselineGroup.length && finlandBaselineGroup.every((v, i) => entry[i] === v)) {
    return null;
  }
  const first = pickedAttribute(entry[0]);
  if (!first) return null;
  return entry.every((member) => pickedAttribute(member) === first) ? first : null;
}

/**
 * Disjunctive facet counts: a facet's counts are computed as if its OWN picks were not applied,
 * while every other filter (and that facet's `NOT` exclusions) still is. Without this, picking
 * "Helsinki" made every other location read the Helsinki count, and picking "Remote" left it the
 * only work-setup option, so users could not see or add an alternative ("Remote or Hybrid").
 *
 * Works on the facetFilters the main query already uses (Algolia-shaped strings), so the extra
 * queries reuse exactly the same filter building (Finland baseline, hidden-job exclusions,
 * exclude_/only_ handling). One request per facet with a pick; facets without picks keep the main
 * search's counts. Numeric filters are not touched, pass them through unchanged on the caller side.
 */
export function buildDisjunctiveFacetRequests(
  facetFilters: readonly FacetFilterEntry[] | undefined,
): DisjunctiveFacetRequest[] {
  if (!facetFilters?.length) return [];
  const finlandBaselineGroup = buildFinlandBaselineFacetFilter(true) as string[];
  const picked = facetFilters.map((entry) => entryPickedAttribute(entry, finlandBaselineGroup));
  const requests: DisjunctiveFacetRequest[] = [];
  for (const attribute of FACET_ATTRIBUTES) {
    if (!picked.includes(attribute)) continue;
    requests.push({
      attribute,
      facetFilters: facetFilters.filter((_, i) => picked[i] !== attribute),
    });
  }
  return requests;
}

/**
 * Replaces the facet counts a disjunctive request supplied, when that request succeeded. Each
 * request asks for a single facet, so its `facets` holds exactly that facet under its index field
 * name (`location` comes back as `locationList`), which is why this merges by the returned keys.
 */
export function mergeDisjunctiveFacets(
  base: Record<string, Record<string, number>> | undefined,
  results: ReadonlyArray<Record<string, Record<string, number>> | null | undefined>,
): Record<string, Record<string, number>> {
  const merged: Record<string, Record<string, number>> = { ...(base ?? {}) };
  for (const facets of results) {
    if (!facets) continue;
    for (const [key, counts] of Object.entries(facets)) {
      if (counts) merged[key] = counts;
    }
  }
  return merged;
}
